import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { Search, Filter, Mail, Phone, Briefcase, GraduationCap, MapPin, X, User } from "lucide-react";
import { db } from "../../firebase";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

import SelectMenu from "../../components/crm/SelectMenu";
// Admin-controlled ordering / visibility config (written from jradmin)
const DIRECTORY_CONFIG_PATH = ["adminConfig", "lawyerDirectory"];

const isLawStudent = (d) =>
  (d?.userCateogry || d?.userCategory || "").toString().trim().toLowerCase() ===
  "law student";

// settings booleans: true => lawyer chose to SHOW that field to other users
const canShow = (lawyer, key) => lawyer?.settings?.[key] === true;

function LawyerCard({ lawyer }) {
  const [imgError, setImgError] = useState(false);
  const showMobile = canShow(lawyer, "mobileNumber") && (lawyer.mobileWithCountryCode || lawyer.mobileNumber);
  const showEmail = canShow(lawyer, "email") && lawyer.email;
  const showLocation = canShow(lawyer, "location") && lawyer.location;
  const hasProfilePic = lawyer.profile && !imgError;

  return (
    <div className="bg-gray-800 rounded-xl p-5 flex flex-col gap-3 hover:bg-gray-700/80 transition border border-gray-700">
      <div className="flex items-center gap-4">
        {hasProfilePic ? (
          <img
            src={lawyer.profile}
            alt={lawyer.name}
            className="w-16 h-16 rounded-full object-cover border-2 border-yellow-500"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-gray-700 border-2 border-yellow-500 flex items-center justify-center">
            <User className="w-8 h-8 text-gray-300" />
          </div>
        )}
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-white truncate">{lawyer.name || "Unnamed"}</h3>
          {lawyer.userCategory || lawyer.userCateogry ? (
            <span className="text-xs text-gray-400">{lawyer.userCategory || lawyer.userCateogry}</span>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-2 text-sm text-gray-300">
        {lawyer.type ? (
          <div className="flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-yellow-500 shrink-0" />
            <span className="truncate">{lawyer.type}</span>
          </div>
        ) : null}
        {lawyer.degree ? (
          <div className="flex items-center gap-2">
            <GraduationCap className="w-4 h-4 text-yellow-500 shrink-0" />
            <span className="truncate">{lawyer.degree}</span>
          </div>
        ) : null}
        {lawyer.experience ? (
          <div className="flex items-center gap-2 text-gray-400">
            <span className="text-xs">Experience: {lawyer.experience} yrs</span>
          </div>
        ) : null}
        {showLocation ? (
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-yellow-500 shrink-0" />
            <span className="truncate">{lawyer.location}</span>
          </div>
        ) : null}
      </div>

      {(showMobile || showEmail) && (
        <div className="border-t border-gray-700 pt-3 flex flex-col gap-2 text-sm">
          {showMobile ? (
            <a
              href={`tel:${lawyer.mobileWithCountryCode || lawyer.mobileNumber}`}
              className="flex items-center gap-2 text-blue-400 hover:text-blue-300"
            >
              <Phone className="w-4 h-4 shrink-0" />
              <span className="truncate">{lawyer.mobileWithCountryCode || lawyer.mobileNumber}</span>
            </a>
          ) : null}
          {showEmail ? (
            <a
              href={`mailto:${lawyer.email}`}
              className="flex items-center gap-2 text-blue-400 hover:text-blue-300"
            >
              <Mail className="w-4 h-4 shrink-0" />
              <span className="truncate">{lawyer.email}</span>
            </a>
          ) : null}
        </div>
      )}
    </div>
  );
}

function SearchLawyer() {
  const [lawyers, setLawyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [fieldFilter, setFieldFilter] = useState("");

  useEffect(() => {
    const fetchLawyers = async () => {
      setLoading(true);
      try {
        // Admin ordering + visibility config (optional — defaults to all visible)
        let order = [];
        let hidden = [];
        try {
          const cfgSnap = await getDoc(doc(db, ...DIRECTORY_CONFIG_PATH));
          if (cfgSnap.exists()) {
            const cfg = cfgSnap.data();
            order = Array.isArray(cfg.order) ? cfg.order : [];
            hidden = Array.isArray(cfg.hidden) ? cfg.hidden : [];
          }
        } catch (e) {
          console.warn("[SearchLawyer] No directory config:", e?.message);
        }
        const hiddenSet = new Set(hidden);
        const orderIndex = new Map(order.map((uid, i) => [uid, i]));

        const snap = await getDocs(collection(db, "lawyers"));
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((l) => l.name && l.name.trim()) // need a name to display
          .filter((l) => !isLawStudent(l)) // practicing lawyers only
          .filter((l) => !hiddenSet.has(l.id)); // admin-hidden excluded

        // Admin-ranked first (by configured order), then the rest alphabetically
        list.sort((a, b) => {
          const ai = orderIndex.has(a.id) ? orderIndex.get(a.id) : Infinity;
          const bi = orderIndex.has(b.id) ? orderIndex.get(b.id) : Infinity;
          if (ai !== bi) return ai - bi;
          return (a.name || "").localeCompare(b.name || "");
        });

        setLawyers(list);
      } catch (e) {
        console.error("[SearchLawyer] Failed to load lawyers:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchLawyers();
  }, []);

  // Distinct practicing fields for the filter dropdown
  const practicingFields = useMemo(() => {
    const set = new Set();
    lawyers.forEach((l) => {
      if (l.type && l.type.trim()) set.add(l.type.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [lawyers]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return lawyers.filter((l) => {
      const matchesName = !term || (l.name || "").toLowerCase().includes(term);
      const matchesField =
        !fieldFilter || (l.type || "").trim().toLowerCase() === fieldFilter.toLowerCase();
      return matchesName && matchesField;
    });
  }, [lawyers, searchTerm, fieldFilter]);

  return (
    <>
      <Navbar />
      <div className="background-circle circle-3 z-0"></div>
      <div className="background-circle circle-4 z-0"></div>
      <main className="px-4 md:px-16 py-8 min-h-screen">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-yellow-500">Search Lawyer</h1>
          <p className="text-gray-400 mt-2">
            Find lawyers by name or filter by their practicing field.
          </p>
        </div>

        {/* Search + Filter controls */}
        <div className="flex flex-col md:flex-row gap-4 max-w-4xl mx-auto mb-8">
          <div className="relative flex-1">
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by lawyer name..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-10 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="relative md:w-72">
            <Filter className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <SelectMenu
              value={fieldFilter}
              onChange={(e) => setFieldFilter(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-3 text-white focus:outline-none focus:border-yellow-500 appearance-none"
            >
              <option value="">All Practicing Fields</option>
              {practicingFields.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </SelectMenu>
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <p className="text-center text-gray-400">Loading lawyers...</p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-gray-400">
            No lawyers found{searchTerm || fieldFilter ? " for your search." : "."}
          </p>
        ) : (
          <>
            <p className="text-center text-gray-500 text-sm mb-4">
              {filtered.length} lawyer{filtered.length !== 1 ? "s" : ""} found
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
              {filtered.map((lawyer) => (
                <LawyerCard key={lawyer.id} lawyer={lawyer} />
              ))}
            </div>
          </>
        )}
      </main>
      <Footer />
    </>
  );
}

export default SearchLawyer;
